import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Plus, Settings2, ArrowLeft } from "lucide-react";
import { useProfiles } from "@/context/ProfileContext";
import { ProfileAvatar, ProfileEditor } from "@/components/family/ProfileEditor";
import { ProfileManager } from "@/components/ProfileManager";
import { HouseholdWeather } from "@/components/HouseholdWeather";
import { CocoonMark } from "@/components/CocoonMark";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ft } from "@/lib/familyText";

export function ProfileSelect() {
  useTranslation(); // subscribe to language changes so ft() re-renders
  const T = ft();
  const { profiles, max, selectProfile, createProfile } = useProfiles();
  const [managing, setManaging] = useState(false);
  const [creating, setCreating] = useState(false);
  const canAdd = profiles.length < max;

  return (
    <div className="min-h-screen starfield flex flex-col items-center justify-center px-6 py-16" data-testid="profile-select">
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>
      <div className="mb-8 opacity-80">
        <span className="font-display text-2xl"><CocoonMark withMind /></span>
      </div>

      {!managing ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-3xl flex flex-col items-center"
        >
          <h1 className="font-display text-4xl sm:text-5xl text-gradient-orange text-center mb-2" data-testid="profile-select-title">{T.who}</h1>
          <p className="text-foreground/50 text-center mb-12">{T.subtitle}</p>

          <div className="flex flex-wrap items-start justify-center gap-6 sm:gap-8">
            {profiles.map((p, i) => (
              <motion.button
                key={p.id}
                data-testid={`profile-pick-${p.id}`}
                onClick={() => selectProfile(p)}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="group flex flex-col items-center gap-3 focus:outline-none"
              >
                <ProfileAvatar profile={p} size={112} selectable />
                <span className="text-base text-foreground/70 group-hover:text-foreground transition-colors truncate max-w-[120px]">{p.name}</span>
              </motion.button>
            ))}

            {canAdd && (
              <button
                data-testid="profile-select-add"
                onClick={() => setCreating(true)}
                className="group flex flex-col items-center gap-3 focus:outline-none"
              >
                <div className="w-28 h-28 rounded-2xl border border-dashed border-white/20 flex items-center justify-center text-foreground/40 group-hover:text-primary group-hover:border-primary/50 transition-colors">
                  <Plus size={34} />
                </div>
                <span className="text-base text-foreground/50 group-hover:text-primary transition-colors">{T.add}</span>
              </button>
            )}
          </div>

          <button
            data-testid="profile-manage-toggle"
            onClick={() => setManaging(true)}
            className="mt-14 flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/12 text-sm text-foreground/60 hover:text-primary hover:border-primary/40 transition-colors"
          >
            <Settings2 size={16} />
            {T.manage}
          </button>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl glass border border-primary/20 rounded-[1.75rem] p-6 sm:p-8"
        >
          <button
            data-testid="profile-manage-back"
            onClick={() => setManaging(false)}
            className="flex items-center gap-2 mb-6 text-sm text-foreground/60 hover:text-primary transition-colors"
          >
            <ArrowLeft size={16} /> {T.done}
          </button>
          <ProfileManager />
          <div className="mt-8 pt-6 border-t border-white/10">
            <HouseholdWeather />
          </div>
        </motion.div>
      )}

      {creating && (
        <ProfileEditor
          initial={null}
          onSave={(data) => createProfile(data)}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
